import { db } from '../firebase/client';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import Category from '../models/Category';

const COLLECTION_NAME = 'categories';

/**
 * Get all categories with their subcategories
 * @param {boolean} activeOnly - If true, only return active categories
 * @returns {Promise<Array<Category>>} Array of category objects
 */
export const getAllCategories = async (activeOnly = false) => {
  try {
    const q = query(collection(db, COLLECTION_NAME), orderBy('displayOrder', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const categories = querySnapshot.docs.map(doc => 
      Category.fromFirestore(doc.id, doc.data())
    );
    
    return activeOnly 
      ? categories.filter(category => category.active) 
      : categories;
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
};

/**
 * Get a category by ID
 * @param {string} categoryId - Category ID
 * @returns {Promise<Category|null>} Category object or null if not found
 */
export const getCategoryById = async (categoryId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, categoryId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return Category.fromFirestore(docSnap.id, docSnap.data());
    }
    
    return null;
  } catch (error) {
    console.error('Error getting category:', error);
    throw error;
  }
};

/**
 * Create a new category
 * @param {Category} categoryData - Category data
 * @returns {Promise<string>} New category ID
 */
export const createCategory = async (categoryData) => {
  try {
    // Generate slug if not provided
    if (!categoryData.slug && categoryData.name) {
      const category = new Category(categoryData);
      categoryData.slug = category.generateSlug();
    }
    
    // Make sure subcategories are properly formatted
    if (categoryData.subcategories && Array.isArray(categoryData.subcategories)) {
      // Ensure each subcategory has required fields with proper formatting
      categoryData.subcategories = categoryData.subcategories.map((subcategory, index) => {
        return {
          id: subcategory.id || index.toString(),
          name: subcategory.name || '',
          description: subcategory.description || '',
          ...(subcategory.price !== undefined ? { price: parseFloat(subcategory.price) || 0 } : {}),
          ...(subcategory.active !== undefined ? { active: subcategory.active } : { active: true })
        };
      });
    }
    
    // Add timestamps if not already present
    // Note: serverTimestamp() is used here which is a special Firestore value
    // It will be converted to the server's timestamp when the document is created
    if (!categoryData.createdAt) {
      categoryData.createdAt = serverTimestamp();
    }
    
    if (!categoryData.updatedAt) {
      categoryData.updatedAt = serverTimestamp();
    }
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), categoryData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

/**
 * Update an existing category
 * @param {string} categoryId - Category ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateCategory = async (categoryId, updates) => {
  try {
    const categoryRef = doc(db, COLLECTION_NAME, categoryId);
    
    // Format subcategories if present in updates
    if (updates.subcategories && Array.isArray(updates.subcategories)) {
      // Ensure each subcategory has required fields with proper formatting
      updates.subcategories = updates.subcategories.map((subcategory, index) => {
        return {
          id: subcategory.id || index.toString(),
          name: subcategory.name || '',
          description: subcategory.description || '',
          ...(subcategory.price !== undefined ? { price: parseFloat(subcategory.price) || 0 } : {}),
          ...(subcategory.active !== undefined ? { active: subcategory.active } : { active: true })
        };
      });
    }
    
    // Update timestamp
    updates.updatedAt = serverTimestamp();
    
    await updateDoc(categoryRef, updates);
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

/**
 * Delete a category
 * @param {string} categoryId - Category ID to delete
 * @returns {Promise<void>}
 */
export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, categoryId));
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

/**
 * Upload a category image
 * @param {string} base64Image - Base64 encoded image
 * @param {string} categoryId - Category ID (optional)
 * @returns {Promise<string>} The same base64 image (for compatibility)
 */
export const uploadCategoryImage = async (base64Image, categoryId) => {
  // Since we're storing images directly in Firestore, this function
  // doesn't need to do anything except return the image data
  return base64Image;
};

/**
 * Initialize default categories if none exist
 * @returns {Promise<void>}
 */
export const initializeDefaultCategories = async () => {
  try {
    const existingCategories = await getAllCategories();
    
    // Only initialize if no categories exist
    if (existingCategories.length === 0) {
      const defaultCategories = [
        {
          name: 'Birthday Decorations',
          description: 'Beautiful decorations for birthday celebrations',
          subcategories: [
            { id: "0", name: 'Kids Birthday', description: 'Fun decorations for kids' },
            { id: "1", name: 'Adult Birthday', description: 'Elegant decorations for adults' },
            { id: "2", name: 'Milestone Birthday', description: 'Special decorations for milestone birthdays' }
          ],
          displayOrder: 1,
          active: true
        },
        {
          name: 'Anniversary Decorations',
          description: 'Romantic decorations for anniversary celebrations',
          subcategories: [
            { id: "0", name: 'Silver Anniversary', description: 'Silver-themed decorations' },
            { id: "1", name: 'Golden Anniversary', description: 'Gold-themed decorations' },
            { id: "2", name: 'Romantic Anniversary', description: 'Romantic setup for couples' }
          ],
          displayOrder: 2,
          active: true
        },
        {
          name: 'Welcome Baby',
          description: 'Decorations for welcoming a new baby',
          subcategories: [
            { id: "0", name: 'Baby Boy', description: 'Blue-themed decorations' },
            { id: "1", name: 'Baby Girl', description: 'Pink-themed decorations' },
            { id: "2", name: 'Gender Neutral', description: 'Neutral-themed decorations' }
          ],
          displayOrder: 3,
          active: true
        },
        {
          name: 'Kids Theme',
          description: 'Theme-based decorations for kids',
          subcategories: [
            { id: "0", name: 'Cartoon Characters', description: 'Popular cartoon character themes' },
            { id: "1", name: 'Superhero', description: 'Superhero-themed decorations' },
            { id: "2", name: 'Princess', description: 'Princess-themed decorations' }
          ],
          displayOrder: 4,
          active: true
        },
        {
          name: 'Baby Shower',
          description: 'Decorations for baby shower celebrations',
          subcategories: [
            { id: "0", name: 'Gender Reveal', description: 'Gender reveal themed decorations' },
            { id: "1", name: 'Traditional', description: 'Traditional baby shower decorations' },
            { id: "2", name: 'Modern', description: 'Modern baby shower decorations' }
          ],
          displayOrder: 5,
          active: true
        },
        {
          name: 'Premium Decorations',
          description: 'High-end premium decorations for special events',
          subcategories: [
            { id: "0", name: 'Luxury', description: 'Luxury high-end decorations' },
            { id: "1", name: 'Custom Design', description: 'Custom-designed decorations' },
            { id: "2", name: 'VIP Setup', description: 'VIP event decorations' }
          ],
          displayOrder: 6,
          active: true
        },
        {
          name: 'Bachelorette Decorations',
          description: 'Fun decorations for bachelorette parties',
          subcategories: [
            { id: "0", name: 'Classic', description: 'Classic bachelorette decorations' },
            { id: "1", name: 'Party Theme', description: 'Themed party decorations' },
            { id: "2", name: 'Elegant', description: 'Elegant bachelorette decorations' }
          ],
          displayOrder: 8,
          active: true
        }
      ];
      
      // Create each category
      for (const categoryData of defaultCategories) {
        // Create a Category instance to ensure consistent data structure
        const category = new Category(categoryData);
        const firestoreData = category.toFirestore();
        
        // Double-check that subcategory IDs are preserved
        if (categoryData.subcategories && Array.isArray(categoryData.subcategories)) {
          firestoreData.subcategories = categoryData.subcategories.map(sub => {
            if (typeof sub === 'string') {
              return sub;
            }
            return {
              ...sub,
              id: sub.id // Ensure ID is preserved exactly as is
            };
          });
        }
        
        await createCategory(firestoreData);
      }
    }
  } catch (error) {
    console.error('Error initializing default categories:', error);
    throw error;
  }
};

export default {
  getAllCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
  uploadCategoryImage,
  initializeDefaultCategories
}; 